/**
 * O FORMATO DOS DADOS — o que uma ficha de pesagem guarda.
 *
 * Uma FICHA é uma negociação inteira: de quem é o gado (origem), para onde vai
 * (destino), em que condições foi comprado (lotes de negociação) e o que a
 * balança disse (registros). Um negócio de 60 cabeças que sai em três
 * caminhões, em três dias, é UMA ficha e UM romaneio — cada registro guarda o
 * próprio dia e hora.
 *
 * O PREÇO MORA NO LOTE, não na categoria nem no animal. «18 bezerros a
 * R$ 1.900 e 6 bezerros a R$ 1.600» são dois lotes da mesma categoria. Cada
 * registro de pesagem aponta para um lote.
 */

export type Base = 'arroba' | 'kg' | 'cabeca'
export type Finalidade = 'Compra' | 'Venda' | 'Conferência'
/** aberta: pesando · encerrada: romaneio emitido · cancelada: fica no ficheiro, fora dos totais */
/** excluida: ficha aberta por engano e sem pesagem; some das listas, mas o banco guarda */
export type Situacao = 'aberta' | 'encerrada' | 'cancelada' | 'excluida'
/** curral: balança do curral · caminhao: balanção, caminhão vazio e carregado */
export type Forma = 'curral' | 'caminhao'

export interface Gps {
  lat: number
  lon: number
  /** metros — o GPS diz quanto confia na própria leitura */
  precisao: number
  em: string
}

export interface Produtor {
  id: string
  nome: string
  doc: string
  ie: string
  propriedade: string
  endereco: string
  municipio: string
  uf: string
  telefone: string
  /** localização da propriedade: marcada uma vez, reaproveitada nas próximas fichas */
  gps: Gps | null
  alteradoEm: string
  /** false = tirado do cadastro (nada se apaga no banco) */
  ativo?: boolean
  /**
   * 3B · já foi cliente numa porteira: aparece primeiro na lista de clientes.
   * O cadastro é o mesmo dos produtores (a fazenda que vende hoje compra
   * amanhã) — nada de tabela nova para um nome e um município.
   */
  cliente?: boolean
  /**
   * 3D · um lugar do próprio gestor (sítio, fazenda onde recria): aparece na
   * lista de nomes da porteira «Meu estoque». Mora no mesmo cadastro, como o
   * cliente, para sincronizar sem migração e não duplicar o mesmo lugar.
   */
  meuLocal?: boolean
  /**
   * R2B · Para pagar o produtor sem procurar o papel: chave PIX, banco,
   * agência e conta. Moram no documento do cadastro (sem migração) e aparecem
   * na tela Contas, ao lado do que falta pagar.
   */
  pix?: string
  banco?: string
  agencia?: string
  conta?: string
}

export interface Destino {
  nome: string
  municipio: string
  uf: string
}

export interface Lote {
  id: string
  categoria: string
  base: Base
  /** R$ por @, por kg ou por cabeça, conforme a base. Nulo: negócio acertado fora. */
  preco: number | null
  /** % de carcaça. Só entra na conta quando a base é arroba. */
  rendimento: number
  /**
   * 3D · descontos DESTE lote, em % do bruto do lote (vacas com 2% + 1,63%,
   * bezerros só com o funrural). Ausente (lote de ficha antiga): vale o
   * `financeiroPct`/`funruralPct` da ficha. Ver `descontosDoLote` em contas.ts.
   */
  financeiroPct?: number
  funruralPct?: number
  /**
   * R1C · «Quebra (%)» combinada com o vendedor: sai do peso da balança ANTES
   * da arroba (e do kg, no lote por kg). O papel mostra o peso da balança, a
   * quebra e o peso que vale. Ausente ou zero (ficha antiga): nada muda.
   */
  quebraPct?: number
}

/**
 * R1C · Como ESTE registro foi pesado (revisão de 06/10/2026: o caminhão
 * aparecia no meio de uma pesagem na balança e obrigava a voltar duas vezes à
 * Negociação, porque a forma valia para a ficha inteira). Ausente (registro
 * antigo): vale o que os campos dizem — tara preenchida é caminhão; senão,
 * a forma da ficha (balanção = peso líquido; curral = balança).
 */
export type ModoDePesagem = 'balanca' | 'caminhao' | 'liquido'

export interface Registro {
  id: string
  /** número de ordem dentro da ficha, nunca reaproveitado */
  n: number
  loteId: string
  /** data e hora da pesagem */
  em: string
  qtd: number
  /** peso dos animais. Nulo só em lote por cabeça, onde o peso é opcional. */
  kg: number | null
  /** caminhão no balanção: vazio e carregado; kg = bruto − tara */
  tara: number | null
  bruto: number | null
  /** caminhão pesado só vazio, esperando voltar carregado: fora de todos os totais */
  pendente: boolean
  obs: string
  /**
   * 3B · a porteira (destino) por onde este gado saiu do curral. Ausente
   * (registro antigo, ou ficha sem apartação) ou de porteira que não existe
   * mais: conta na PRIMEIRA porteira da ficha. Assim nada some de conta.
   */
  destinoId?: string
  /** R1C · forma deste registro (a pesagem repete a do último) */
  modo?: ModoDePesagem
}

/**
 * 3B · PORTEIRA DE APARTAÇÃO — para onde o gado vai. Não é o lote: o lote é o
 * que se combinou com o VENDEDOR (preço, base, rendimento); a porteira é a
 * decisão do GESTOR na balança. Os dois se escolhem separados na pesagem.
 *
 *  - cliente (INTERMEDIAÇÃO, 3F): fazenda ou pessoa para quem o gestor compra.
 *    O cliente paga o vendedor DIRETO; a receita do gestor é só a «comissão a
 *    receber (cliente)», lançada em tabela própria (o comprador nunca vê). O
 *    nome do cliente o comprador vê: precisa saber para onde vai o caminhão;
 *  - estoque: «Meu estoque» — entra na Catira, sem comissão de cliente;
 *  - revenda (COMPRA E VENDA COM ÁGIO, 3F): o gestor paga o vendedor e vende ao
 *    comprador final, sem o gado descer do caminhão. O preço de venda NÃO mora
 *    aqui (a ficha sincroniza para o comprador): mora na tabela `vendas_revenda`
 *    (migração 012), lote a lote. Ágio = venda − compra − gastos − comissão a pagar.
 *
 * Mora no documento da ficha (jsonb), sem migração. Ficha sem porteiras se
 * comporta como uma porteira só, implícita: quem não aparta não percebe nada.
 */
export type TipoDePorteira = 'cliente' | 'estoque' | 'revenda'

export interface Porteira {
  id: string
  tipo: TipoDePorteira
  /** cliente: nome do cliente · estoque: «Meu estoque» · revenda: o comprador final */
  nome: string
  /** cliente escolhido no cadastro (produtor marcado como cliente) — cópia do dia, como o produtor da ficha */
  clienteId?: string
  municipio?: string
  uf?: string
  /**
   * 3B (ANTIGO) · «Revenda direta» com o valor total digitado. Desde a 3F o
   * preço de venda mora fora da ficha (`VendaDaPorteira`); este campo só é
   * LIDO, como venda «por valor total», até o gestor pôr preço por lote — e
   * aí sai do documento. Ficha nova nunca grava.
   */
  valorVenda?: number | null
  obs?: string
  /** 3D · «Meu estoque» com nome escolhido do cadastro (produtor marcado `meuLocal`) */
  localId?: string
  /**
   * 3D · nº da GTA desta porteira. A GTA sai depois da pesagem, uma por
   * destino (cada caminhão tem a sua), e se anota no passo do Romaneio.
   */
  gta?: string
}

/**
 * 3B · «Comissão a receber (cliente)»: o cliente paga ao gestor, por cabeça da
 * porteira dele. É RECEITA do gestor (vai para o relatório P3). Não confundir
 * com a `Comissao` da 2D, que é «a pagar (comprador)». Só gestor lança; gestor
 * e Consulta veem; o comprador nem recebe o dado (tabela própria, migração 010).
 */
export interface ComissaoCliente {
  fichaId: string
  destinoId: string
  porCabeca: number
  obs: string
  lancadaEm: string
}

/**
 * 3F · O preço de venda de UM lote da compra, numa porteira Revenda: o mesmo
 * lote (mesmos animais, mesmos pesos), com a base, o preço, o rendimento e os
 * descontos combinados com o COMPRADOR FINAL — no padrão da 3D.
 */
export interface LoteDeVenda {
  /** o lote da compra (Lote.id) a que este preço se aplica */
  loteId: string
  base: Base
  /** R$ por @, por kg ou por cabeça. Nulo: ainda sem preço (o papel avisa). */
  preco: number | null
  rendimento: number
  financeiroPct: number
  funruralPct: number
}

/**
 * 3F · A tabela de venda de uma porteira Revenda. Mora em tabela própria
 * (migração 012), NUNCA no documento da ficha: a ficha sincroniza para o
 * comprador (funcionário), e ele não pode ver o preço de venda. Só o gestor
 * grava; gestor e Consulta veem.
 */
export interface VendaDaPorteira {
  fichaId: string
  destinoId: string
  lotes: LoteDeVenda[]
  obs: string
  lancadaEm: string
  /**
   * R1A · a venda «por valor total» da porteira antiga («Revenda direta»),
   * que a 013 tirou de dentro da ficha e trouxe para cá. Vale enquanto não há
   * preço por lote.
   */
  valorTotal?: number | null
}

/**
 * 3F · Dinheiro que o comprador final pagou ao gestor por uma porteira
 * Revenda (de uma vez ou em várias). Mesmo jeito da quitação: errado, anula
 * com motivo; nada se apaga. Mesma regra de quem vê da tabela de venda.
 */
export interface Recebimento {
  id: string
  fichaId: string
  destinoId: string
  valor: number
  recebidoEm: string
  obs: string
  lancadoEm: string
  anuladoEm: string | null
  anuladoMotivo: string
  /** ficam no aparelho até subir ao R2 (2G), como os da quitação */
  comprovantes: Comprovante[]
  /** o servidor recusou de vez: fica só aqui, riscado e fora do «recebido» */
  recusado?: string
  /** R1A · quem lançou e quem anulou (a Consulta também dá baixa) */
  lancadoPorNome?: string
  anuladoPorNome?: string
}

export interface Comprovante {
  id: string
  nome: string
  tipo: string
  tamanho: number
}

/**
 * Parcela COMBINADA (a condição de pagamento): quanto e quando. Comprador ou
 * gestor lançam, e ela viaja dentro da ficha. O pagamento EFETIVADO não mora
 * aqui: é a quitação, só do gestor, em tabela própria (src/dados/pagamentos.ts).
 */
export interface Pagamento {
  id: string
  valor: number
  vencimento: string
  obs: string
  /**
   * Etapa 1 (antes do banco): «pago em» e comprovantes eram anotados na
   * parcela, por qualquer um. Não contam mais como pago — o gestor confirma
   * e vira quitação. Ficam só para leitura.
   */
  pagoEm?: string
  comprovantes?: Comprovante[]
}

/** Como se combinou pagar: a forma (PIX, boleto…) e o que mais foi dito. Prazo e parcelas são os `pagamentos`. */
export interface Condicao {
  forma: string
  obs: string
}

/** Pagamento efetivado — só o gestor lança; errado, anula com motivo (nada se apaga). */
export interface Quitacao {
  id: string
  fichaId: string
  /** parcela a que se refere, quando há; vazio = conta no total */
  parcelaId: string
  valor: number
  pagoEm: string
  obs: string
  lancadaEm: string
  anuladaEm: string | null
  anuladaMotivo: string
  /** arquivos ficam neste aparelho (IndexedDB) até a tarefa 2G levá-los ao R2 */
  comprovantes: Comprovante[]
  /**
   * O servidor recusou de vez: nunca entrou no banco. Fica só neste aparelho,
   * riscada e fora do «pago», com a frase do servidor (e os comprovantes).
   */
  recusada?: string
  /** R1A · quem lançou e quem anulou (a Consulta também dá baixa) */
  lancadaPorNome?: string
  anuladaPorNome?: string
  /** R2B · de que conta saiu o dinheiro (opcional, texto livre: «Sicredi PJ») — migração 014 */
  contaOrigem?: string
}

/**
 * «Comissão a pagar (comprador)»: por cabeça, lançada pelo gestor depois do
 * negócio; o gestor paga ao comprador dele no acerto (2F). O comprador nunca vê.
 */
export interface Comissao {
  fichaId: string
  porCabeca: number
  obs: string
  lancadaEm: string
  /** acerto do extrato em que foi paga ao comprador: preenchida, o valor não muda mais */
  acertoId?: string | null
  /**
   * R1A · as cabeças da ficha quando a comissão foi lançada (o banco anota,
   * migração 013). É esse número que vai para o acerto: reescrever a ficha
   * depois não aumenta a comissão. Ausente: comissão só deste aparelho.
   */
  cabecas?: number | null
  /**
   * R2B · Para quem é a comissão do comprador: vazio = o comprador que pesou a
   * ficha (paga no acerto do extrato); um nome = um corretor (nome livre), pago
   * fora do acerto. Migração 014.
   */
  beneficiario?: string
}

/** Gasto do comprador que o vendedor não vê: comissão, frete, vacina… Valor total. */
export interface Custo {
  id: string
  descricao: string
  valor: number
  /** 3B · de que porteira é o gasto (frete de cada caminhão). Ausente: divide pelas cabeças. */
  destinoId?: string
  /**
   * R2B · O gasto vira conta a pagar (tela Contas): para quem, quando vence,
   * quando foi pago e o comprovante. Moram no documento da ficha, como o gasto
   * (sem migração); quem grava é quem lança o gasto (gestor; comprador na ficha
   * dele). Sem `pagoEm`, o gasto está «a pagar».
   */
  favorecido?: string
  vencimento?: string
  pagoEm?: string
  /** de que conta saiu o dinheiro (opcional) */
  contaOrigem?: string
  comprovantes?: Comprovante[]
  /** R2B · gasto lançado errado: ANULA com motivo (fica riscado, fora das somas) — nada se apaga */
  anuladoEm?: string
  anuladoMotivo?: string
  anuladoPor?: string
}

/**
 * «Custo fechado» (2E): alguém com permissão de gravar conferiu o que pagar e
 * para quem. Guarda o total SEM comissão daquele momento (o comprador também
 * fecha, e ele não sabe da comissão): se o romaneio ou os gastos mudarem
 * depois, o total não bate e a ficha volta a «custo pendente» sozinha.
 * Fechar e reabrir também ficam no histórico — nada se apaga.
 */
export interface CustoFechado {
  em: string
  /** nome de quem fechou */
  por: string
  /** vendedor + gastos da ficha, sem comissão, no dia do fechamento */
  semComissao: number
}

export interface Evento {
  em: string
  oque: string
  /** negociação como era antes de uma alteração — a versão anterior do romaneio */
  antes?: Negociacao
}

export interface Emitente {
  nome: string
  doc: string
  telefone: string
  cidade: string
}

/**
 * A parte da ficha que é acordo, e por isso pode ser corrigida depois do romaneio pronto.
 * 3D: os descontos passaram para cada lote; os da ficha ficam como padrão do
 * lote que não tem o seu (ficha antiga). A tela não os mostra mais.
 */
export interface Negociacao {
  lotes: Lote[]
  financeiroPct: number
  funruralPct: number
}

export interface Ficha extends Negociacao {
  id: string
  /** ANO-0001, sequencial por aparelho */
  numero: string
  data: string
  criadaEm: string
  alteradaEm: string
  situacao: Situacao
  finalidade: Finalidade
  /** cópia do produtor no dia: mudar o cadastro depois não reescreve o romaneio */
  produtor: Produtor
  /**
   * 3D · saiu da tela de Origem: o destino agora são as porteiras (Negociação)
   * e a GTA é por porteira (Romaneio). Ficha antiga mostra o que tem aqui como
   * antes. Ficha nova grava vazio — e não deixa de gravar: um celular com o app
   * antigo lê `f.destino.nome` e quebraria com o campo ausente.
   */
  destino: Destino
  /** GTA da ficha sem porteiras (ou a antiga, anotada na abertura) */
  gta: string
  obs: string
  forma: Forma
  registros: Registro[]
  /** parcelas combinadas (prazo e valor de cada uma) */
  pagamentos: Pagamento[]
  /** forma de pagamento combinada; ausente em ficha antiga */
  condicao?: Condicao
  custos: Custo[]
  /** ausente ou nulo = custo ainda não conferido (ficha encerrada mostra «custo pendente») */
  custoFechado?: CustoFechado | null
  historico: Evento[]
  /** sobe a cada alteração da negociação depois de encerrada */
  versao: number
  /** quem emitiu, gravado ao encerrar */
  emitente: Emitente | null
  /** quem pesou e negociou — sai no romaneio («Pesado por») */
  pesadoPor?: string
  /** 3B · porteiras de apartação (montadas na Negociação). Ausente ou vazia: uma porteira só, implícita. */
  porteiras?: Porteira[]
  /** rev que o servidor conhece; ausente = ainda não subiu nenhuma vez */
  revServidor?: number
  /**
   * R1C · A REGRA DA ARROBA desta ficha. true (ficha nova desde a R1C): a
   * arroba de cada lote é arredondada em 2 casas ANTES de multiplicar pelo
   * preço — é o número que o papel mostra, e quem refaz a conta à mão chega ao
   * mesmo valor (33,68 @ × R$ 320 = R$ 10.777,60). Ausente (ficha antiga): a
   * conta de sempre, com a arroba inteira — nenhuma ficha antiga muda um centavo.
   */
  arrobaEmDuasCasas?: boolean
  /**
   * R1A · só no aparelho (não vai ao banco): o `alteradaEm` de cada versão
   * mandada desde a última resposta do servidor. Se a resposta se perdeu (sinal
   * fraco), o próximo envio diz ao servidor «a versão que você tem pode ser
   * minha» — e ele reconhece o reenvio em vez de acusar conflito.
   */
  enviosSemResposta?: string[]
  /**
   * R2B · Quem abriu a ficha era o gestor (pesou ele mesmo): a ficha não
   * oferece «Comissão do comprador» (só a de um corretor). Ausente em ficha
   * antiga: vale a sigla do número.
   */
  pesadoPorGestor?: boolean
  /**
   * R2D · «Venda do meu estoque»: a venda que o gestor faz meses depois, no
   * sítio dele, do gado que está no Meu estoque. Só vale com finalidade
   * «Venda». Ao encerrar, a venda sai sozinha do estoque, categoria por
   * categoria (sem marcar nada depois), e os lotes usam as categorias do
   * estoque. Para o banco, o romaneio, as notas e o relatório é uma Venda
   * como outra qualquer — por isso é uma marca, e não uma finalidade nova.
   * Ausente: venda comum (a marca «Sai do meu estoque» continua na aba Custo).
   */
  vendaDoEstoque?: boolean
}

export interface Ajustes {
  emitente: Emitente
  categorias: string[]
  /** padrões para fichas novas — nunca mexem em ficha existente */
  base: Base
  /** rendimento das categorias sem padrão próprio */
  rendimento: number
  /** R1C · rendimento padrão por categoria (lote novo daquela categoria já nasce com ele) */
  rendimentos?: Record<string, number>
  /** R1C · as categorias novas (Novilho, Vaca com cria…) já foram acrescentadas neste aparelho */
  categoriasR1?: boolean
  financeiroPct: number
  funruralPct: number
  tema: 'auto' | 'claro' | 'escuro'
  ultimaCopia: string | null
}

export const BASES: Record<Base, { nome: string; unidade: string }> = {
  arroba: { nome: 'Por arroba (@)', unidade: 'R$/@' },
  kg: { nome: 'Por kg de peso vivo', unidade: 'R$/kg' },
  cabeca: { nome: 'Por cabeça', unidade: 'R$/cab.' },
}

/**
 * `ajuda` é a frase do gestor; `ajudaComprador`, a do comprador (R1C, revisão
 * de 06/10/2026): o comprador não lê sobre comissão, ágio nem revenda — ele só
 * precisa saber quem paga o vendedor e para onde vai o gado.
 */
export const TIPOS_DE_PORTEIRA: Record<TipoDePorteira, { nome: string; ajuda: string; ajudaComprador: string }> = {
  cliente: { nome: 'Cliente (intermediação)', ajuda: 'compro para uma fazenda ou pessoa: ela paga o vendedor direto e eu recebo comissão por cabeça',
    ajudaComprador: 'Cliente: a fazenda paga o vendedor direto' },
  estoque: { nome: 'Meu estoque', ajuda: 'o gado fica comigo, no meu estoque', ajudaComprador: 'Meu estoque: o gado vai para a empresa' },
  revenda: { nome: 'Revenda', ajuda: 'eu pago o vendedor e vendo ao comprador final com ágio; nem desce do caminhão',
    ajudaComprador: 'Revenda: a empresa paga o vendedor e vende o gado adiante' },
}

/**
 * R1C · Os nomes dos dois descontos nas telas e no papel (decisão do gestor,
 * 06/10/2026). O Funrural aqui é só DESCONTO ADMINISTRATIVO: o gestor repassa
 * ao vendedor o desconto que leva do frigorífico — nada de «a recolher». A
 * conta não muda; os nomes internos (`financeiroPct`, `funruralPct`) ficam.
 */
export const NOME_FINANCEIRO = 'Desconto à vista (financeiro)'
export const NOME_FUNRURAL = 'Desconto de Funrural (repasse do frigorífico)'

export const SITUACOES: Record<Situacao, string> = {
  aberta: 'em andamento',
  encerrada: 'encerrada',
  cancelada: 'cancelada',
  excluida: 'excluída',
}

/**
 * R2B · Dinheiro que o CLIENTE (intermediação) pagou ao gestor pela comissão
 * de uma porteira — igual ao recebimento da revenda: data, valor, comprovante;
 * errado, anula com motivo. Tabela `recebimentos_comissao_cliente` (migração
 * 014). Só gestor e Consulta lançam e veem; o comprador nem recebe.
 */
export type RecebimentoDeComissao = Recebimento

/**
 * R2B · A comissão do comprador paga FORA do acerto do extrato (ao corretor,
 * ou ao comprador por PIX antes do acerto). Tabela `pagamentos_comissao`
 * (migração 014). Só gestor e Consulta lançam e veem. Comissão com pagamento
 * valendo não entra no acerto (o banco confere).
 */
export interface PagamentoDeComissao {
  id: string
  fichaId: string
  valor: number
  pagoEm: string
  obs: string
  contaOrigem?: string
  lancadoEm: string
  anuladoEm: string | null
  anuladoMotivo: string
  comprovantes: Comprovante[]
  recusado?: string
  lancadoPorNome?: string
  anuladoPorNome?: string
}

/** R2B · Os dois nomes fixos da comissão, em todas as telas e papéis (revisão de 06/10/2026). */
export const NOME_COMISSAO_COMPRADOR = 'Comissão do comprador (você paga)'
export const NOME_COMISSAO_CLIENTE = 'Comissão do cliente (você recebe)'
